import type {
  CourseKeyAlertItem,
  CourseKeyConfig,
  CourseKeyContentStructured,
  CourseKeyContentTabs,
  CourseKeyPolicyItem,
  CourseKeyPlatform,
  CourseKeyStepItem,
} from '@/types/course-key';

export const MINDTAP_COURSE_KEY_TOKEN = '{{MINDTAP_COURSE_KEY}}';

const OVERVIEW_NOTE_STYLES = `<style>
  * {
    box-sizing: border-box;
  }

  body {
    margin: 0;
    background: #f7f6fa;
    font-family: "Inter", "Segoe UI", Arial, sans-serif;
  }

  .student-note {
    width: 690px;
    max-width: 100%;
    background: linear-gradient(135deg, #30175f 0%, #3b1e79 45%, #5a30b3 100%);
    border-radius: 22px;
    padding: 24px 26px;
    color: #ffffff;
  }

  .student-note h2 {
    margin: 0 0 12px;
    font-size: 28px;
    line-height: 1.15;
    font-weight: 800;
    letter-spacing: -0.3px;
  }

  .student-note p {
    margin: 0 0 20px;
    font-size: 18px;
    line-height: 1.45;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.78);
  }

  .note-steps {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .note-step {
    display: flex;
    align-items: center;
    min-height: 43px;
    padding: 10px 14px;
    border-radius: 15px;
    background: rgba(255, 255, 255, 0.13);
    border: 1px solid rgba(255, 255, 255, 0.18);
    font-size: 16px;
    line-height: 1.35;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.95);
  }

  @media (max-width: 520px) {
    .student-note {
      padding: 20px;
      border-radius: 18px;
    }

    .student-note h2 {
      font-size: 24px;
    }

    .student-note p {
      font-size: 16px;
    }

    .note-step {
      font-size: 15px;
    }
  }
</style>`;

const MINDTAP_OVERVIEW_HTML = `${OVERVIEW_NOTE_STYLES}
<section class="student-note">
  <h2>Important note for students</h2>
  <p>
    Please read the course key instructions carefully. This course key explains how to log in, complete your assigned coursework, apply for graduation, and receive your certificate through your student portal.
  </p>
  <div class="note-steps">
    <div class="note-step">1. Understand the process of logging into your coursework.</div>
    <div class="note-step">2. Complete the coursework assigned to you.</div>
    <div class="note-step">3. Apply for graduation with all applicable requirements. Once approved, you will receive your certificate in your student portal.</div>
  </div>
</section>`;

const ISPRING_OVERVIEW_HTML = `${OVERVIEW_NOTE_STYLES}
<section class="student-note">
  <h2>Important note for students</h2>
  <p>
    Please read the course key instructions carefully. This course key explains how to log in,
    complete your theory coursework, schedule clinicals, submit your completion information,
    and move toward certification registration.
  </p>
  <div class="note-steps">
    <div class="note-step">1. Understand the process of logging into your coursework.</div>
    <div class="note-step">2. Complete the theory portion before scheduling clinicals.</div>
    <div class="note-step">3. Submit graduation and completion documents when eligible.</div>
    <div class="note-step">4. Register for the certification exam after receiving your completion certificate.</div>
  </div>
</section>`;

export function emptyContentTabs(): CourseKeyContentTabs {
  return {
    overviewHtml: '',
    processHtml: '',
    loginDetailsHtml: '',
    policiesHtml: '',
    supportHtml: '',
    instructionsHtml: '',
    loginStepsHtml: '',
  };
}

export function defaultContentTabs(platform: CourseKeyPlatform): CourseKeyContentTabs {
  const tabs = emptyContentTabs();
  if (platform === 'mindtap') {
    tabs.overviewHtml = MINDTAP_OVERVIEW_HTML;
    tabs.instructionsHtml = `<p>Welcome to your course. Please read all instructions carefully.</p><p>Use course key ${MINDTAP_COURSE_KEY_TOKEN} when prompted.</p>`;
    tabs.processHtml =
      '<ol><li>Log in and complete the coursework assigned to you.</li><li>Apply for graduation once coursework and program requirements are complete.</li></ol>';
    tabs.loginStepsHtml =
      '<p>After activation, visit Cengage and sign in with the account you created during registration.</p>';
    tabs.policiesHtml =
      '<p>Review inactive status, extension, and payment policies before starting.</p>';
    tabs.supportHtml =
      '<p><strong>Need help?</strong> Create a support ticket from your Student Dashboard.</p>';
    return tabs;
  }
  tabs.overviewHtml = ISPRING_OVERVIEW_HTML;
  tabs.processHtml =
    '<ol><li>Complete your theory portion online.</li><li>Schedule clinicals from your Student Dashboard.</li><li>Submit graduation when eligible.</li></ol>';
  tabs.loginDetailsHtml =
    '<p>Your username and password are shown automatically in the Login Details tab once you view login.</p>';
  tabs.policiesHtml =
    '<p>Review inactive status, extension, and payment policies before starting.</p>';
  tabs.supportHtml =
    '<p><strong>Need help?</strong> Create a support ticket from your Student Dashboard.</p>';
  return tabs;
}

export function contentTabsFromConfig(config: CourseKeyConfig): CourseKeyContentTabs {
  const defaults = defaultContentTabs(config.platform);
  const stored = config.contentTabs;
  if (stored && Object.values(stored).some((value) => value?.trim())) {
    const merged = { ...emptyContentTabs(), ...stored };
    if (!merged.overviewHtml?.trim()) {
      merged.overviewHtml = defaults.overviewHtml;
    }
    return merged;
  }
  const instructions = config.instructionsHtml?.trim() || config.rulesHtml?.trim() || '';
  const tabs = { ...defaults };
  if (config.platform === 'mindtap') {
    tabs.instructionsHtml = instructions || tabs.instructionsHtml;
    return tabs;
  }
  tabs.overviewHtml = config.instructionsHtml?.trim() || tabs.overviewHtml;
  tabs.policiesHtml = config.rulesHtml?.trim() || tabs.policiesHtml;
  return tabs;
}

export function emptyContentStructured(): CourseKeyContentStructured {
  return {
    instructionsSteps: [],
    loginStepsSteps: [],
    instructionsAlerts: [],
    processSteps: [],
    policyItems: [],
    supportItems: [],
  };
}

function hasSupportItems(items: CourseKeyPolicyItem[] | undefined): boolean {
  return Boolean(items?.some((s) => s.title?.trim() || s.bodyHtml?.trim()));
}

const DEFAULT_SUPPORT_ITEMS: CourseKeyPolicyItem[] = [
  {
    title: 'Technical support related to login or payments',
    bodyHtml:
      '<p>Helpful tip for getting questions answered faster: please create a support ticket from your Student Dashboard. This helps our dedicated support staff track, review, assign, and respond to your request in the most organized way.</p>',
    variant: 'default',
  },
  {
    title: 'Support Ticket Guidance',
    bodyHtml:
      '<p>To help us assist you in the most organized and streamlined way, submit any class, login, portal, or course-related questions through a support ticket in your Student Dashboard.</p>',
    variant: 'default',
  },
  {
    title: 'Graduation Application Reminder',
    bodyHtml:
      '<p>Once you have completed and applicable requirements, you can apply for graduation from your Student Dashboard or create a support ticket for help.</p>',
    variant: 'default',
  },
];

export function defaultContentStructured(platform: CourseKeyPlatform): CourseKeyContentStructured {
  if (platform === 'mindtap') {
    return {
      instructionsSteps: [
        {
          title: 'Go to the enrollment website',
          bodyHtml:
            '<p>Click and go to <a href="https://www.getenrolled.com" target="_blank" rel="noopener noreferrer">www.getenrolled.com</a>.</p>',
        },
        {
          title: 'Enter your course key',
          bodyHtml: `<p>Use course key <strong>${MINDTAP_COURSE_KEY_TOKEN}</strong> when prompted.</p>`,
        },
        {
          title: 'Continue and follow the instructions',
          bodyHtml:
            '<p>Follow the on-screen instructions and complete your Cengage subscription setup.</p>',
        },
        {
          title: 'Create or connect your account',
          bodyHtml:
            '<p>You may be required to obtain your own Cengage subscription. The account will connect your course with the school.</p>',
        },
      ],
      loginStepsSteps: [
        {
          title: 'Visit Cengage',
          bodyHtml:
            '<p>Go to <a href="https://cengage.com" target="_blank" rel="noopener noreferrer">cengage.com</a>.</p>',
        },
        {
          title: 'Click Login',
          bodyHtml:
            '<p>Select the login option and enter the account you created during registration.</p>',
        },
        {
          title: 'Access your dashboard',
          bodyHtml:
            '<p>You will be redirected to your dashboard where your courses should appear on screen.</p>',
        },
      ],
      instructionsAlerts: [
        {
          title: 'Course completion window',
          bodyHtml:
            '<p>For this program, you have <strong>4 months</strong> to complete the assigned coursework.</p>',
          variant: 'warning',
        },
      ],
      processSteps: [
        {
          title: 'Course Work',
          bodyHtml: '<p>Log in and complete the coursework assigned to you.</p>',
        },
        {
          title: 'Graduation',
          bodyHtml:
            '<p>Once you complete the coursework assigned to you and any other applicable requirements to fulfill your program, you can apply for graduation. Once approved, you will receive your certificate in your portal.</p>',
        },
      ],
      policyItems: [
        {
          title: 'Inactive Status',
          bodyHtml:
            '<p> More than 6 months of inactivity may result in deactivation.</p>',
          variant: 'default',
        },
        {
          title: 'Extension Option',
          bodyHtml:
            '<p>The login is active only for the duration of access. An extension can be applied to the access duration for $50 for 2 additional weeks only.</p>',
          variant: 'default',
        },
        {
          title: 'Deactivation Due to Non-Payment',
          bodyHtml:
            '<p>If one or more auto-pay payments are declined, the school may deactivate access until payments are up to date.</p>',
          variant: 'warning',
        },
        {
          title: 'Payment Plan Responsibility',
          bodyHtml:
            '<p>Payment plan is a courtesy option. If more than one payment is declined, the student may be required to pay the entire tuition balance to resume the course. Students must track payments and ensure payments are made according to the selected plan.</p>',
          variant: 'default',
        },
      ],
      supportItems: DEFAULT_SUPPORT_ITEMS,
    };
  }

  return {
    instructionsSteps: [],
    loginStepsSteps: [],
    instructionsAlerts: [],
    processSteps: [
      {
        title: 'Theory Portion',
        bodyHtml:
          '<p>Log in and complete your theory portion online using the username and password provided in this course key.</p>',
      },
      {
        title: 'Clinical Scheduling',
        bodyHtml:
          '<p>After completing your theory portion online, schedule clinicals from your Student Dashboard under Forms and Documents.</p>',
      },
      {
        title: 'Graduation Application',
        bodyHtml:
          '<p>Once theory, clinicals, and tuition balance are complete, submit the graduation form or create a support ticket.</p>',
      },
      {
        title: 'Certification Registration',
        bodyHtml:
          '<p>After receiving your completion certificate, register for the certification exam using the link sent to your email.</p>',
      },
      {
        title: 'Close Your Certification Application',
        bodyHtml:
          '<p>After attempting and passing the certification exam, close your certification exam application so your certifications can be processed.</p>',
        wide: true,
      },
    ],
    policyItems: [
      {
        title: 'Inactive Status',
        bodyHtml:
          '<p> More than 6 months of inactivity may result in deactivation.</p>',
        variant: 'default',
      },
      {
        title: 'Extension Option',
        bodyHtml:
          '<p>The login is active only for the duration of access. An extension can be applied to the access duration for $50 for 2 additional weeks only.</p>',
        variant: 'default',
      },
      {
        title: 'Deactivation Due to Non-Payment',
        bodyHtml:
          '<p>If one or more auto-pay payments are declined, the school may deactivate access until payments are up to date.</p>',
        variant: 'warning',
      },
      {
        title: 'Payment Plan Responsibility',
        bodyHtml:
          '<p>Payment plan is a courtesy option. If more than one payment is declined, the student may be required to pay the entire tuition balance to resume the course. Students must track payments and ensure payments are made according to the selected plan.</p>',
        variant: 'default',
      },
    ],
    supportItems: DEFAULT_SUPPORT_ITEMS,
  };
}

export function contentStructuredFromConfig(
  config: { contentStructured?: Partial<CourseKeyContentStructured>; platform: CourseKeyPlatform }
): CourseKeyContentStructured {
  const defaults = defaultContentStructured(config.platform);
  const stored = config.contentStructured;
  const hasData =
    stored &&
    [
      ...(stored.instructionsSteps ?? []),
      ...(stored.loginStepsSteps ?? []),
      ...(stored.instructionsAlerts ?? []),
      ...(stored.processSteps ?? []),
      ...(stored.policyItems ?? []),
      ...(stored.supportItems ?? []),
    ].some((item) => {
      const step = item as CourseKeyStepItem;
      const alert = item as CourseKeyAlertItem;
      const policy = item as CourseKeyPolicyItem;
      return (
        step.title?.trim() ||
        step.bodyHtml?.trim() ||
        alert.title?.trim() ||
        alert.bodyHtml?.trim() ||
        policy.title?.trim() ||
        policy.bodyHtml?.trim()
      );
    });

  if (hasData && stored) {
    const merged = { ...emptyContentStructured(), ...stored };
    if (!hasSupportItems(merged.supportItems)) {
      merged.supportItems = defaults.supportItems;
    }
    return merged;
  }
  return defaults;
}
